import { transition, trigger } from '@angular/animations';
import { CdkScrollable } from '@angular/cdk/overlay';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  CommonMediaQueries,
  DIALOG_DATA,
  DialogRef,
  IconModule,
  MediaScreenService,
} from '@hglobal/fin-ui';
import { TranslateModule } from '@ngx-translate/core';

import {
  IwSwipeGestureDirective,
  SwipeDirectionEnum,
} from '@iw/core/directives';
import { DialogData } from '@iw/core/entities';

import { left, right } from './base-dialog.animations';
import { ButtonComponent } from '../button/button.component';

const FIRST_COMPONENT = 0;
const STEP = 1;

@Component({
  selector: 'iw-base-dialog',
  imports: [
    IconModule,
    ButtonComponent,
    NgComponentOutlet,
    TranslateModule,
    CdkScrollable,
    IwSwipeGestureDirective,
    NgTemplateOutlet,
  ],
  templateUrl: './base-dialog.component.html',
  styleUrl: './base-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.full-width]': 'data.fullWidth && this.isDesktop()',
    '[class.full-screen]': 'this.data.fullScreen && !this.isDesktop()',
    '[class.on-top]': 'data.isOnTop',
    '[style.--dialog-width]': 'data.width ? data.width + "px" : null',
  },
  animations: [
    trigger('slider', [
      transition(':increment', right),
      transition(':decrement', left),
    ]),
  ],
})
export class BaseDialogComponent<T, V> {
  readonly #mediaScreenService = inject(MediaScreenService);
  readonly dialogRef = inject(DialogRef);

  swipeDirectionEnum = SwipeDirectionEnum;

  data = inject<DialogData<T, V>>(DIALOG_DATA);

  isDesktop = toSignal(
    this.#mediaScreenService.mediaMatcher(CommonMediaQueries.MD),
  );

  index = FIRST_COMPONENT;

  get component() {
    return this.data.components[this.index];
  }

  get count(): number {
    return this.data.components.length;
  }

  get title(): string {
    const title: string | string[] | undefined = this.data.title;
    const showActiveStepTitle = this.data?.showActiveStepTitle ?? false;

    if (Array.isArray(title)) {
      if (showActiveStepTitle) {
        return title[this.index] ?? '';
      } else if (Array.isArray(title)) {
        return title.slice(0, this.index + 1).join(' / ');
      }
    }

    return title ?? '';
  }

  get isFirstStep(): boolean {
    return this.index === FIRST_COMPONENT;
  }

  get isLastStep(): boolean {
    return this.index === this.count - 1;
  }

  onSwipeDown(): void {
    this.close();
  }

  next(currentStepData?: any): void {
    if (this.isLastStep) {
      delete this.data['prevComponentData'];

      return;
    }

    if (currentStepData) {
      this.data.prevComponentData = currentStepData;
    } else if (!currentStepData && this.data.prevComponentData) {
      delete this.data['prevComponentData'];
    }

    this.index += STEP;
  }

  prev(): void {
    if (this.data.prevComponentData) {
      delete this.data['prevComponentData'];
    }

    if (this.isFirstStep) {
      return;
    }

    this.index -= STEP;
  }

  moveTo(i: number, currentStepData?: T): void {
    if (i > this.count - 1) {
      return;
    }

    if (currentStepData) {
      this.data.prevComponentData = currentStepData;
    } else if (!currentStepData && this.data.prevComponentData) {
      delete this.data['prevComponentData'];
    }

    this.index = i;
  }

  close(): void {
    this.dialogRef.close();
  }
}
